
import { setupWakeLock } from './utils/wakeLock.js';
import { FaceLandmarker, PoseLandmarker, FilesetResolver } from "@mediapipe/tasks-vision";
import { ThreeScene } from './scene/threeScene.js';
import { KeypointsManagerFace } from './managers/resultsManagerFace.js';
import { KeypointsManagerPose } from './managers/resultsManagerPose.js';
import { VideoSourceManager } from './managers/videoSourceManager.js';
import { RecordingManager } from './managers/recordingManager.js';
import { MediaPipeBenchmark } from './utils/mediapipeBenchmark.js';
import { Loader } from './gui/loader.js';
import { DetectionOverlay } from './gui/detectionOverlay.js';
import { FpsCounter } from './utils/fpsCounter.js';
import { FullscreenManager } from './gui/fullscreenManager.js';
import { CameraSelector } from './gui/cameraSelector.js';
import { VideoSelector } from './gui/videoSelector.js';
//import { GUIManager } from './managers/guiManager.js';
// WakeLock doit être appelé ici, mais tout le reste est utilisé dans le code (pas d'import inutile détecté)
setupWakeLock();


// Initialisation globale uniquement si nécessaire pour l'UI (évite la pollution du window)
if (!window.loader) {
    window.loader = new Loader();
    if (!window.detectionOverlayInstance) {
        window.detectionOverlayInstance = new DetectionOverlay();
    }
    window.detectionOverlay = window.detectionOverlayInstance;
    window.fullscreenManager = new FullscreenManager();
    window.CameraSelector = CameraSelector;
    window.VideoSelector = VideoSelector;
    const project_settings = window.SCENE_CONFIG;


    // =============================================================================
    // INITIALISATION DES COMPOSANTS
    // =============================================================================

    // Initialiser le compteur FPS
    let fpsCounter = null;
    if (project_settings.fpsVisible) {
        fpsCounter = new FpsCounter();
    }

    // Initialiser le gestionnaire de source vidéo
    const videoSourceManager = new VideoSourceManager();

    // Initialiser le benchmark MediaPipe
    const mediapipeBenchmark = new MediaPipeBenchmark();

    // Initialiser le gestionnaire d'interface graphique
    let guiManager = null;
    let recordingManager = null;

    let keypointsManagerFace = null;
    let keypointsManagerPose = null;

    let faceLandmarker;
    let poseLandmarker;
    let width, height;
    let lastVideoTime = -1;
    let resultsFace = undefined;
    let resultsPose = undefined;
    let threeScene = null;
    let sceneSettings = null;

    // Variables pour gérer la boucle d'animation
    let animationFrameId = null;
    let isAnimationRunning = false;

    // Canvas 
    const videoInsert = document.getElementById('videoInsert');
    const videoCanvas = document.getElementById('videoCanvas');
    const maskCanvas = document.getElementById('maskCanvas');
    const threejsCanvas = document.getElementById('threejsCanvas');

    // Canvas downscalé pour MediaPipe (performances)
    const downScaleVideoCanvas = document.createElement('canvas');
    videoInsert.setAttribute('playsinline', '');
    videoInsert.setAttribute('autoplay', '');
    videoInsert.setAttribute('muted', '');


    // Correction Safari iOS : forcer la lecture vidéo après interaction utilisateur
    function isSafariIOS() {
        return (
            /iP(ad|hone|od)/.test(navigator.userAgent) &&
            /WebKit/.test(navigator.userAgent) &&
            !/CriOS/.test(navigator.userAgent)
        );
    }

    function forceVideoPlayOnUserInteraction() {
        if (!videoInsert) return;
        const playHandler = () => {
            videoInsert.play().catch(() => { });
            // Pour Safari iOS, relancer la boucle d'animation si besoin
            if (!isAnimationRunning) {
                startPredictionLoop();
            }
            // Retirer l'écouteur après la première interaction
            window.removeEventListener('touchstart', playHandler);
            window.removeEventListener('click', playHandler);
        };
        window.addEventListener('touchstart', playHandler, { once: true });
        window.addEventListener('click', playHandler, { once: true });
    }

    initApp();
    forceVideoPlayOnUserInteraction();

    async function initApp() {
        window.loader.show('Initializing...');
        // Import dynamique du fichier de settings JS
        try {
            const module = await import(`./settings/${project_settings.settingsFile || 'sceneSettings'}.js`);
            sceneSettings = module.sceneSettings;
        } catch (error) {
            console.error('Erreur lors du chargement de sceneSettings:', error);
            sceneSettings = [];
        }

        // Mettre à jour faceDetect et poseDetect selon la scène courante
        const currentScene = sceneSettings[project_settings.initialSceneIndex];
        project_settings.faceDetect = currentScene?.faceDetect ?? false;
        project_settings.poseDetect = currentScene?.poseDetect ?? false;


        // Initialiser le gestionnaire d'interface graphique après chargement des settings
        if (project_settings.guiVisible) {
            // guiManager = new GUIManager(project_settings, mediapipeBenchmark, videoSourceManager);
            // // Mettre à jour l'affichage du facteur de réduction dans l'interface après ajustement automatique
            // if (guiManager.gui) {
            //     // Forcer la mise à jour des contrôles GUI pour refléter le nouveau facteur
            //     guiManager.gui.controllersRecursive().forEach(controller => {
            //         if (controller.property === 'scaleFactorMediaPipe') {
            //             controller.updateDisplay();
            //         }
            //     });
            // }
        }
        // Initialiser le landmarker après chargement des settings
        createFaceLandmarker();
    }

    async function createFaceLandmarker() {
        window.loader.setMessage('Initializing MediaPipe...');
        const filesetResolver = await FilesetResolver.forVisionTasks("./wasm");
        const optimalDelegate = await mediapipeBenchmark.getOptimalDelegate();

        if (project_settings.faceDetect) {
            //console.log('🔍 Initialisation du FaceLandmarker');
            window.loader.setMessage('Setting up face detector...');
            faceLandmarker = await FaceLandmarker.createFromOptions(filesetResolver, {
                baseOptions: {
                    modelAssetPath: 'models/face_landmarker.task',
                    delegate: optimalDelegate
                },
                runningMode: "VIDEO",
                smoothlandmarks: true,
                numFaces: 1,
                outputFacialTransformationMatrixes: true,
                outputFaceBlendshapes: true
            });
        }

        if (project_settings.poseDetect) {
            window.loader.setMessage('Setting up pose detector...');
            poseLandmarker = await PoseLandmarker.createFromOptions(filesetResolver, {
                baseOptions: {
                    modelAssetPath: 'models/pose_landmarker_full.task',
                    delegate: optimalDelegate
                },
                runningMode: "VIDEO",
                numPoses: 1, 
                minPoseDetectionConfidence: 0.5,
                minPosePresenceConfidence: 0.5,
                minTrackingConfidence: 0.5,
                outputSegmentationMasks: true
            });
        }    // Initialiser l'interface graphique
        if (guiManager) guiManager.init(adjustCanvas);



        // Initialiser le gestionnaire de source vidéo
        window.loader.setMessage('Initialisation de la caméra...');
        videoSourceManager.init(videoInsert).onVideoLoaded((videoWidth, videoHeight) => {
            width = videoWidth;
            height = videoHeight;

            // Calculer automatiquement le scale factor basé sur la résolution vidéo
            // Formule: Max(width, height) / 640
            const maxDimension = Math.max(width, height);
            const autoScaleFactor = Math.max(1, Math.ceil(maxDimension / 320));
            project_settings.scaleFactorMediaPipe = autoScaleFactor;

            //console.log(`📏 Résolution vidéo: ${width}x${height}`);
            //console.log(`⚡ Scale factor MediaPipe calculé automatiquement: ${autoScaleFactor} (Max dimension: ${maxDimension})`);

            // Mettre à jour l'affichage dans l'interface si elle existe
            if (guiManager && guiManager.gui) {
                guiManager.gui.controllersRecursive().forEach(controller => {
                    if (controller.property === 'scaleFactorMediaPipe') {
                        controller.updateDisplay();
                    }
                });
            }

            // Nettoyer l'ancienne scène si elle existe - approche complète
            if (threeScene) {
                //console.log('🧹 Nettoyage complet de l\'ancienne scène Three.js');
                threeScene.dispose(); // Appeler dispose() avant de mettre à null
                threeScene = null;
            }

            // Arrêter l'ancienne boucle d'animation si elle existe
            if (animationFrameId) {
                cancelAnimationFrame(animationFrameId);
                animationFrameId = null;
                isAnimationRunning = false;
                //console.log('🔄 Ancienne boucle d\'animation arrêtée');
            }

            // Reset du compteur FPS pour éviter les valeurs erronées
            if (fpsCounter) fpsCounter.reset();

            // Attendre un peu pour que le nettoyage soit terminé avant de créer la nouvelle scène
            setTimeout(() => {
                // Créer une nouvelle scène avec l'index initial depuis PHP
                //console.log(`🎯 Initialisation de ThreeScene avec l'index: ${project_settings.initialSceneIndex}`);
                window.loader.setMessage('Initialisation de la scène 3D...');
                threeScene = new ThreeScene(
                    threejsCanvas,
                    videoCanvas,
                    maskCanvas,
                    videoInsert,
                    project_settings,
                    width,
                    height,
                    project_settings.initialSceneIndex,
                    sceneSettings // Passer la variable sceneSettings
                );

                keypointsManagerFace = new KeypointsManagerFace(videoCanvas, videoInsert, width, height);
                keypointsManagerPose = new KeypointsManagerPose(videoCanvas, videoInsert, width, height, threeScene.scene);

                // Initialiser le gestionnaire d'enregistrement
                recordingManager = new RecordingManager(threejsCanvas, videoCanvas);
                recordingManager.setThreeScene(threeScene); // Passer la référence à la scène
                //console.log('🎥 RecordingManager initialisé avec background intelligent');

                // Lancer la nouvelle boucle d'animation
                if (!isAnimationRunning) {
                    startPredictionLoop();
                }
                adjustCanvas();
                window.loader.setMessage('Application prête !');

                // Configurer le message de l'overlay selon le type de détection
                if (window.detectionOverlayInstance) {
                    let overlayMessage = '';
                    let overlayType = 'face'; // par défaut

                    if (project_settings.faceDetect && project_settings.poseDetect) {
                        overlayMessage = 'Position your face and body in the frame';
                        overlayType = 'pose'; // utiliser l'overlay pose pour les deux
                    } else if (project_settings.faceDetect) {
                        overlayMessage = 'Position your face in the frame';
                        overlayType = 'face';
                    } else if (project_settings.poseDetect) {
                        overlayMessage = 'Position your body in the frame';
                        overlayType = 'pose';
                    }

                    window.detectionOverlayInstance.setOverlayType(overlayType);
                    window.detectionOverlayInstance.setMessage(overlayMessage);
                    // Ne plus forcer l'affichage au démarrage
                }

                setTimeout(() => {
                    window.loader.hide();
                }, 1000);
            }, 50); // Petit délai pour s'assurer que le nettoyage est terminé
        })
            .onError((error) => {
                console.error('Erreur avec la source vidéo:', error);
                stopPredictionLoop(); // Arrêter la boucle d'animation
                if (guiManager) guiManager.updateAnimationState(animationFrameId, isAnimationRunning);
                if (fpsCounter) fpsCounter.pause(); // Pause le compteur en cas d'erreur
            });

        // Charger la liste des webcams disponibles pour le GUI
        try {
            const devices = await videoSourceManager.getAvailableDevices();
            if (guiManager) guiManager.configureWebcamController(devices);
        } catch (error) {
            console.warn('Impossible de charger les webcams:', error);
        }

        // Démarrer avec la webcam par défaut
        try {
            window.loader.setMessage('Starting webcam...');
            await videoSourceManager.startWebcam();
        } catch (error) {
            console.warn('Impossible de démarrer la webcam par défaut:', error);
            // Lancer quand même la boucle pour être prêt
            if (!isAnimationRunning) {
                startPredictionLoop();
            }
        }

        // Mettre à jour les informations de benchmark dans le GUI
        if (guiManager) guiManager.updateBenchmarkInfo();

        // Initialiser le sélecteur de caméra
        if (window.CameraSelector) {
            window.cameraSelector = new window.CameraSelector(videoSourceManager);

            // Connecter le callback pour mettre à jour l'icône
            videoSourceManager.onCameraChange((facingMode) => {
                window.cameraSelector.updateCameraInfo(facingMode);
            });

            //console.log('📷 Sélecteur de caméra initialisé');
        }

        // Initialiser le sélecteur de vidéo
        if (window.VideoSelector) {
            window.videoSelector = new window.VideoSelector(videoSourceManager);

            // Connecter le callback pour mettre à jour l'état du sélecteur
            videoSourceManager.onVideoSourceChange((sourceType) => {
                window.videoSelector.updateVideoSourceInfo(sourceType);
            });

            //console.log('🎬 Sélecteur de vidéo initialisé');
        }

        // Connecter le callback pour la gestion automatique du flip
        videoSourceManager.onFlipStateChange((shouldFlip, facingMode) => {
            //console.log(`🔄 Flip automatique: ${shouldFlip ? 'ON' : 'OFF'} (${facingMode || 'non détecté'})`);

            // Mettre à jour project_settings.isSelfieMode automatiquement
            if (videoSourceManager.isAutoFlipEnabled()) {
                const previousSelfieMode = project_settings.isSelfieMode;
                project_settings.isSelfieMode = shouldFlip;

                if (previousSelfieMode !== shouldFlip) {
                    //console.log(`📱 Mode selfie automatiquement ${shouldFlip ? 'activé' : 'désactivé'}`);

                    // Mettre à jour l'interface GUI si disponible
                    if (guiManager && guiManager.gui) {
                        guiManager.gui.controllersRecursive().forEach(controller => {
                            if (controller.property === 'isSelfieMode') {
                                controller.updateDisplay();
                            }
                        });
                    }
                }
            }
        });

        // Log informatif sur les nouvelles fonctionnalités
        // console.log('\n📱 === FLIP AUTOMATIQUE ACTIVÉ ===');
        // console.log('✅ Caméra AVANT (selfie) → Flip automatique ON');
        // console.log('❌ Caméra ARRIÈRE → Flip automatique OFF');
        // console.log('📁 Fichiers/URLs → Flip automatique OFF');
        // console.log('🔧 Contrôle manuel dans GUI → Sources Vidéo > Flip Auto');
        // console.log('=================================\n');

        // Ajouter quelques utilitaires globaux pour le debug/test
        // window.flipUtils = {
        //     enableAutoFlip: () => videoSourceManager.setAutoFlip(true),
        //     disableAutoFlip: () => videoSourceManager.setAutoFlip(false),
        //     getFlipInfo: () => videoSourceManager.getFlipInfo(),
        //     testCamera: async (deviceId = null) => {
        //         console.log('🧪 Test de caméra...');
        //         try {
        //             await videoSourceManager.startWebcam(deviceId);
        //             const info = videoSourceManager.getFlipInfo();
        //             console.log('📊 Info flip:', info);
        //         } catch (error) {
        //             console.error('❌ Erreur test caméra:', error);
        //         }
        //     }
        // };

        // console.log('🛠️ Utils flip disponibles: window.flipUtils');
        // console.log('   • window.flipUtils.getFlipInfo() - Infos sur le flip');
        // console.log('   • window.flipUtils.enableAutoFlip() - Activer flip auto');
        // console.log('   • window.flipUtils.disableAutoFlip() - Désactiver flip auto');


    }

    async function predictWebcam() {
        const ctx = videoCanvas.getContext("2d");
        const downScaleCtx = downScaleVideoCanvas.getContext("2d");
        const maskCtx = maskCanvas.getContext("2d");

        // Vérifier que la vidéo est prête avant de continuer
        // readyState >= 2 signifie HAVE_CURRENT_DATA ou plus (la frame actuelle est disponible)
        if (!videoInsert || videoInsert.readyState < 2 || videoInsert.videoWidth === 0 || videoInsert.videoHeight === 0) {
            // Vidéo pas encore prête, réessayer au prochain frame
            if (isAnimationRunning) {
                animationFrameId = window.requestAnimationFrame(predictWebcam);
            }
            return;
        }

        // Correction Safari iOS : s'assurer que la vidéo est en lecture
        if (isSafariIOS() && videoInsert.paused) {
            videoInsert.play().catch(() => { });
        }

        // Canvas pleine résolution pour Three.js
        videoCanvas.width = width;
        videoCanvas.height = height;
        ctx.clearRect(0, 0, width, height);
        ctx.drawImage(videoInsert, 0, 0, width, height);

        // Facteur de réduction pour MediaPipe (plus petit = plus rapide)
        const scaleFactor = project_settings.scaleFactorMediaPipe;
        const downScaleWidth = Math.floor(width / scaleFactor);
        const downScaleHeight = Math.floor(height / scaleFactor);

        // Vérifier que les dimensions sont valides
        if (downScaleWidth <= 0 || downScaleHeight <= 0) {
            console.warn('⚠️ Dimensions invalides pour MediaPipe:', downScaleWidth, 'x', downScaleHeight);
            if (isAnimationRunning) {
                animationFrameId = window.requestAnimationFrame(predictWebcam);
            }
            return;
        }

        // Canvas downscalé pour MediaPipe
        downScaleVideoCanvas.width = downScaleWidth;
        downScaleVideoCanvas.height = downScaleHeight;
        downScaleCtx.clearRect(0, 0, downScaleWidth, downScaleHeight);
        downScaleCtx.drawImage(videoInsert, 0, 0, downScaleWidth, downScaleHeight);

        const startTimeMs = performance.now();


        // Correction Safari iOS : forcer le rafraîchissement du flux vidéo
        // (Safari iOS ne met parfois à jour qu'après un "nudge" du src)
        if (isSafariIOS() && videoInsert.readyState < 2) {
            // Hack : réaffecter le src pour forcer la mise à jour
            const src = videoInsert.srcObject;
            videoInsert.srcObject = null;
            videoInsert.srcObject = src;
        }

        if (lastVideoTime !== videoInsert.currentTime) {
            lastVideoTime = videoInsert.currentTime;

            let coordsFace = null;
            let coordsPose = null;
            let hasDetection = false;

            if (project_settings.faceDetect && faceLandmarker) {
                resultsFace = faceLandmarker.detectForVideo(downScaleVideoCanvas, startTimeMs);
                coordsFace = keypointsManagerFace.detectedFaceUpdate(resultsFace, project_settings, width, height);

                // Vérifier si un visage est détecté
                if (resultsFace && resultsFace.faceLandmarks && resultsFace.faceLandmarks.length > 0) {
                    hasDetection = true;
                }
            }
            if (project_settings.poseDetect && poseLandmarker) {
                resultsPose = poseLandmarker.detectForVideo(downScaleVideoCanvas, startTimeMs);

                coordsPose = keypointsManagerPose.detectedPoseUpdate(resultsPose, project_settings, width, height);

                // Vérifier si une pose est détectée
                if (resultsPose && resultsPose.landmarks && resultsPose.landmarks.length > 0) {
                    hasDetection = true;
                }

                // Afficher le masque de segmentation si disponible (optimisé)
                // Note: Check du GUI supprimé pour toujours afficher le masque (debug)
                if (resultsPose && resultsPose.segmentationMasks && resultsPose.segmentationMasks.length > 0) {

                    const segmentationMask = resultsPose.segmentationMasks[0];
                    const maskData = segmentationMask.getAsUint8Array();

                    // Créer un canvas temporaire pour le masque réduit
                    const tempCanvas = document.createElement('canvas');
                    const tempCtx = tempCanvas.getContext('2d');
                    tempCanvas.width = downScaleWidth;
                    tempCanvas.height = downScaleHeight;

                    // Créer l'imageData pour la taille réduite
                    const tempImageData = tempCtx.createImageData(downScaleWidth, downScaleHeight);

                    for (let i = 0; i < maskData.length; i++) {
                        const pixelIndex = i * 4;
                        const value = maskData[i]; // Valeurs déjà normalisées 0-255

                        tempImageData.data[pixelIndex] = value;     // Rouge
                        tempImageData.data[pixelIndex + 1] = value; // Vert  
                        tempImageData.data[pixelIndex + 2] = value; // Bleu
                        tempImageData.data[pixelIndex + 3] = 255;   // Alpha
                    }

                    // Afficher le masque réduit sur le canvas temporaire
                    tempCtx.putImageData(tempImageData, 0, 0);

                    // Redimensionner le masque à la taille originale
                    maskCtx.clearRect(0, 0, width, height);
                    maskCtx.drawImage(tempCanvas, 0, 0, downScaleWidth, downScaleHeight, 0, 0, width, height);
                }
            }

            // Création de l'objet regroupant les résultats
            const coordsResults = {
                face: coordsFace,
                pose: coordsPose
            };

            // Mise à jour directe de Three.js
            if (threeScene) {
                threeScene.update(coordsResults, project_settings, width, height);
            }

            // Gérer l'overlay de détection
            if (window.detectionOverlayInstance) {
                if (hasDetection) {
                    window.detectionOverlayInstance.onDetectionActive();
                } else {
                    window.detectionOverlayInstance.onDetectionLost();
                }
            }
        }    // Mettre à jour le compteur FPS
        if (fpsCounter) fpsCounter.update();
        // Continuer la boucle seulement si elle est encore active
        if (isAnimationRunning) {
            animationFrameId = window.requestAnimationFrame(predictWebcam);
        }
    }

    // Fonction pour démarrer la boucle de prédiction
    function startPredictionLoop() {
        if (isAnimationRunning) {
            console.warn('⚠️ Boucle d\'animation déjà en cours');
            return;
        }
        window.loader.setMessage('Démarrage de la détection...');

        // Indiquer que la détection a démarré
        if (window.detectionOverlayInstance) {
            if (typeof window.detectionOverlayInstance.onDetectionStarted === 'function') {
                window.detectionOverlayInstance.onDetectionStarted();
            } else {
                console.error('[ERROR] window.detectionOverlayInstance.onDetectionStarted is not a function. Actual value:', window.detectionOverlayInstance);
            }
        } else {
            console.error('[ERROR] window.detectionOverlayInstance is not defined');
        }

        isAnimationRunning = true;
        if (fpsCounter) fpsCounter.resume(); // S'assurer que le FPS counter est actif
        animationFrameId = window.requestAnimationFrame(predictWebcam);
    }

    // Fonction pour arrêter la boucle de prédiction
    function stopPredictionLoop() {
        if (animationFrameId) {
            cancelAnimationFrame(animationFrameId);
            animationFrameId = null;
        }
        isAnimationRunning = false;
        //console.log('⏸️ Arrêt de la boucle d\'animation');

        // Mettre à jour l'état d'animation dans le GUI
        if (guiManager) guiManager.updateAnimationState(animationFrameId, isAnimationRunning);
        if (fpsCounter) fpsCounter.pause(); // Pause le compteur en cas d'erreur
    }

    // Throttling pour les redimensionnements
    let resizeTimeout;
    let isResizing = false;

    window.addEventListener('resize', () => {
        if (isResizing) return;
        isResizing = true;

        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
            adjustCanvas();
            isResizing = false;
        }, 100);
    });

    function adjustCanvas() {
        const windowRatio = window.innerWidth / window.innerHeight;
        const videoRatio = width / height;
        const adjustedWidth = windowRatio > videoRatio ? window.innerWidth : window.innerHeight * videoRatio;
        const adjustedHeight = windowRatio > videoRatio ? window.innerWidth / videoRatio : window.innerHeight;

        // Déterminer si on doit appliquer le flip
        // Le flip automatique est géré par le VideoSourceManager
        // Mais on garde la logique existante pour la compatibilité
        const currentFacingMode = videoSourceManager.getCurrentCameraFacingMode();
        const shouldFlip = project_settings.isSelfieMode &&
            (videoSourceManager.isFrontCamera() ||
                (!videoSourceManager.isBackCamera() && currentFacingMode === null));

        // Mettre à jour les informations pour le GUI
        if (guiManager) guiManager.updateCameraInfo(
            currentFacingMode ?
                (currentFacingMode === 'user' ? 'Avant (Front)' : 'Arrière (Back)') :
                'Inconnue/Fichier',
            shouldFlip
        );

        const baseTransform = shouldFlip ? "translate(-50%, -50%) scaleX(-1)" : "translate(-50%, -50%)";
        const baseStyles = {
            position: "absolute",
            width: `${adjustedWidth}px`,
            height: `${adjustedHeight}px`,
            top: "50%",
            left: "50%",
            transform: baseTransform
        };

        // Inclure maskCanvas dans le redimensionnement
        [videoInsert, videoCanvas, maskCanvas, threejsCanvas].forEach(element => {
            Object.assign(element.style, baseStyles);
        });

        // Définir les dimensions des canvas
        [videoCanvas, maskCanvas, threejsCanvas].forEach(canvas => {
            canvas.width = width;
            canvas.height = height;
        });

        if (threeScene) {
            threeScene.adjustCanvas(width, height);
        }

        // Mettre à jour les dimensions du canvas de composition pour l'enregistrement
        // (Plus nécessaire avec la nouvelle approche directe)
        // if (recordingManager) {
        //     recordingManager.updateCanvasDimensions(width, height);
        // }
    }
}
